/**
 * Screen Reader Announcer Component
 *
 * Live region for screen reader announcements.
 * WCAG 2.1 AA compliance - Status messages (4.1.3).
 */

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';

type PolitenessLevel = 'polite' | 'assertive' | 'off';

interface AnnouncerContextValue {
  announce: (message: string, politeness?: PolitenessLevel) => void;
}

const AnnouncerContext = createContext<AnnouncerContextValue>({
  announce: () => {},
});

export const useAnnouncer = () => useContext(AnnouncerContext);

interface ScreenReaderAnnouncerProps {
  children: ReactNode;
}

export const ScreenReaderAnnouncer: React.FC<ScreenReaderAnnouncerProps> = ({ children }) => {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  const announce = useCallback((message: string, politeness: PolitenessLevel = 'polite') => {
    if (politeness === 'assertive') {
      setAssertiveMessage(message);
      // Clear after announcement
      setTimeout(() => setAssertiveMessage(''), 100);
    } else if (politeness === 'polite') {
      setPoliteMessage(message);
      // Clear after announcement
      setTimeout(() => setPoliteMessage(''), 100);
    }
  }, []);

  const value: AnnouncerContextValue = {
    announce,
  };

  return (
    <AnnouncerContext.Provider value={value}>
      {children}

      {/* Live regions for screen reader announcements */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {politeMessage}
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        {assertiveMessage}
      </div>
    </AnnouncerContext.Provider>
  );
};

export default ScreenReaderAnnouncer;

/**
 * Hook to announce route changes to screen readers
 */
export const useRouteAnnouncer = (currentRoute: string) => {
  const { announce } = useAnnouncer();

  useEffect(() => {
    // Extract page name from route
    const pageName = currentRoute.split('/').filter(Boolean).pop() || 'home';
    const formattedName = pageName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    announce(`Navigated to ${formattedName} page`, 'polite');
  }, [currentRoute, announce]);
};
